import { useEffect, useState } from 'react';
import { KanbanBoardProps } from './types';
import { KanbanColumn } from './KanbanColumn';
import { useIsMobile } from '@/shared/hooks';
import { DEFAULT_CARD_DENSITY, DENSITY_TOKENS } from './density';
import { KanbanDensityProvider } from './KanbanDensityContext';

/**
 * Board mobile (abas de coluna + UMA coluna por vez, mockup app.html) alinhado
 * ao MESMO corte do shell (<1024): abaixo dele o app já mostra appbar+bottom-nav,
 * então o board também vira mobile — sem estado híbrido entre 768-1024.
 */

/**
 * KanbanBoard Component (Generic & Reusable)
 *
 * A generic Kanban board component that can be used with any data type.
 * Displays items in columns with customizable rendering.
 *
 * @template T - Type of items in the columns
 *
 * @example
 * ```tsx
 * <KanbanBoard
 *   columns={columns}
 *   renderCard={(item) => <MyCard item={item} />}
 *   onCardClick={handleClick}
 *   enableDragDrop={false}
 * />
 * ```
 */
export function KanbanBoard<T>({
  columns,
  renderCard,
  onCardClick,
  enableDragDrop = false,
  onCardMove,
  loading = false,
  emptyMessage = 'Nenhum item encontrado',
  className = '',
  onImport,
  onExport,
  onShowInfo,
  showTotalValue = false,
  onLoadMore,
  enableReorder,
  onCardDrop,
  onIntraColumnDropBlocked,
  onAddCard,
  addCardLabel,
  sumLabel,
  mobileTabStyle = 'dark',
  defaultMobileColumnId,
  density = DEFAULT_CARD_DENSITY
}: KanbanBoardProps<T>) {
  const isMobile = useIsMobile();
  // Coluna ativa no modo mobile (abas). Default: primeira coluna.
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);

  // Troca de densidade fica SUSPENSA enquanto há um arrasto em curso: remedir a
  // coluna no meio do gesto rola a lista sob o ponteiro e o card cai um índice
  // fora. Os eventos nativos `dragstart`/`dragend`/`drop` sobem dos cards até a
  // raiz do board, então o board consegue saber que há arrasto sem prop nova.
  const [isDragging, setIsDragging] = useState(false);
  const [appliedDensity, setAppliedDensity] = useState(density);
  useEffect(() => {
    if (!isDragging) setAppliedDensity(density);
  }, [density, isDragging]);

  // Watchdog: se a origem do arrasto for desmontada pela virtualização (o card
  // sai da janela renderizada no meio do gesto) ou o drop cair fora do board,
  // os listeners nativos no container (dragTracking abaixo) nunca disparam
  // `dragend`/`drop` e `isDragging` fica preso em `true` até o próximo gesto —
  // suspendendo a densidade indefinidamente. `dragend` sempre dispara no
  // elemento de origem mesmo fora do viewport, então escutar no `window`
  // cobre os dois casos sem interferir no fluxo normal.
  useEffect(() => {
    if (!isDragging) return;
    const onWindowDragEnd = () => setIsDragging(false);
    window.addEventListener('dragend', onWindowDragEnd);
    return () => window.removeEventListener('dragend', onWindowDragEnd);
  }, [isDragging]);

  const dragTracking = {
    onDragStart: () => setIsDragging(true),
    onDragEnd: () => setIsDragging(false),
    onDrop: () => setIsDragging(false),
  };

  if (loading) {
    // O esqueleto imita a densidade escolhida: bloco = altura da linha virtual
    // menos o passo, e quantidade ≈ o que cabe em ~560px de coluna.
    const { estimateSize, gap } = DENSITY_TOKENS[density];
    const skeletonRows = Math.max(3, Math.round(560 / estimateSize));
    return (
      <div className="flex gap-4 p-4 h-full overflow-x-auto">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="flex flex-col flex-1 rounded-xl animate-pulse"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
              minWidth: '240px',
              maxWidth: '280px'
            }}
          >
            {/* Cabeçalho da coluna (`.mk-head`) — não acompanha a densidade do card */}
            <div
              className="h-14 m-3 rounded-lg"
              style={{ backgroundColor: 'var(--theme-surface-sunken)' }}
            ></div>
            <div className="flex-1 p-3 flex flex-col" style={{ gap }}>
              {Array.from({ length: skeletonRows }, (_, row) => (
                <div
                  key={row}
                  className="rounded-lg flex-none"
                  style={{ height: estimateSize - gap, backgroundColor: 'var(--theme-surface-sunken)' }}
                ></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--theme-muted-text)' }}>{emptyMessage}</p>
      </div>
    );
  }

  const renderColumn = (column: (typeof columns)[number], mobile: boolean) => (
    <KanbanColumn
      key={column.id}
      id={column.id}
      title={column.title}
      color={column.color}
      count={column.count ?? column.items.length}
      items={column.items}
      renderCard={(item) => renderCard(item, column.id)}
      onCardClick={onCardClick}
      enableDragDrop={enableDragDrop && !mobile}
      onCardMove={onCardMove}
      emptyMessage={emptyMessage}
      totalValue={column.totalValue}
      onImport={onImport}
      onExport={onExport}
      onShowInfo={onShowInfo}
      showTotalValue={showTotalValue}
      hasMore={column.hasMore}
      loadingMore={column.loadingMore}
      loadMoreError={column.loadMoreError}
      onLoadMore={onLoadMore}
      enableReorder={enableReorder && !mobile}
      onCardDrop={onCardDrop}
      onIntraColumnDropBlocked={onIntraColumnDropBlocked}
      onAddCard={onAddCard}
      addCardLabel={addCardLabel}
      sumLabel={sumLabel}
      fluid={mobile}
      bare={mobile}
      breakdown={column.breakdown}
      laneSummary={column.laneSummary}
      battery={column.battery}
    />
  );

  // ── MOBILE (mockup): abas de coluna + UMA coluna por vez (DnD desabilitado) ──
  if (isMobile) {
    const active =
      columns.find((c) => c.id === activeColumnId) ??
      (defaultMobileColumnId
        ? columns.find((c) => c.id === defaultMobileColumnId)
        : undefined) ??
      columns[0];
    return (
      <KanbanDensityProvider density={appliedDensity}>
      <div className={`flex flex-col h-full ${className}`} {...dragTracking}>
        {/* Abas de coluna (.m-tab do mockup): 34px, pill, count em pill próprio;
            ativa = fundo escuro (text-primary) com count translúcido */}
        <div
          className="flex-none flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ padding: '10px 14px 4px' }}
          data-testid="kanban-mobile-tabs"
        >
          {columns.map((column) => {
            const isActive = column.id === active.id;
            const columnCount = column.count ?? column.items.length;
            // Dialeto 'lane-chip' (`.m-lane` do attendance-management):
            // chip 32px, dot 8px da lane, ativa = lilás, count faint sem pill
            if (mobileTabStyle === 'lane-chip') {
              return (
                <button
                  key={column.id}
                  type="button"
                  onClick={() => setActiveColumnId(column.id)}
                  className="flex-none inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition-colors"
                  style={
                    isActive
                      ? {
                          backgroundColor: 'var(--theme-primary-background)',
                          border: '1px solid transparent',
                          color: 'var(--theme-primary)',
                        }
                      : {
                          backgroundColor: 'transparent',
                          border: '1px solid var(--theme-input-border)',
                          color: 'var(--theme-card-text)',
                        }
                  }
                  data-testid={`kanban-mobile-tab-${column.id}`}
                >
                  <span
                    className="flex-none rounded-full"
                    style={{ width: 8, height: 8, backgroundColor: column.color || 'var(--theme-primary)' }}
                  />
                  {column.title}
                  <span className="text-[11px] tabular-nums" style={{ color: 'var(--theme-muted-text)', opacity: 0.8 }}>
                    {columnCount}
                  </span>
                </button>
              );
            }
            return (
              <button
                key={column.id}
                type="button"
                onClick={() => setActiveColumnId(column.id)}
                className="flex-none flex items-center gap-[7px] h-[34px] px-[13px] rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors"
                style={
                  isActive
                    ? {
                        backgroundColor: 'var(--theme-text)',
                        color: 'var(--theme-card-background)',
                        border: '1px solid var(--theme-text)',
                      }
                    : {
                        backgroundColor: 'var(--theme-card-background)',
                        border: '1px solid var(--theme-card-border)',
                        color: 'var(--theme-muted-text)',
                      }
                }
                data-testid={`kanban-mobile-tab-${column.id}`}
              >
                {column.title}
                <span
                  className="text-[11px] font-bold rounded-full tabular-nums"
                  style={{
                    padding: '1px 7px',
                    backgroundColor: isActive ? 'rgba(255,255,255,.2)' : 'var(--theme-surface-sunken)',
                    color: isActive ? '#fff' : 'var(--theme-muted-text)',
                  }}
                >
                  {columnCount}
                </span>
              </button>
            );
          })}
        </div>
        {/* Coluna ativa (`.m-feed`: padding 10px 14px; o rodapé de 88px do mockup
            é coberto pelo pb-[72px] do <main> do shell — não duplicar aqui) */}
        <div className="flex-1 min-h-0 pt-[10px] px-[14px] pb-[10px] flex">
          {renderColumn(active, true)}
        </div>
      </div>
      </KanbanDensityProvider>
    );
  }

  // ── DESKTOP: board horizontal (mockup: padding 16px 20px, colunas 300px) ──
  return (
    <KanbanDensityProvider density={appliedDensity}>
      <div className={`flex gap-[14px] py-4 px-5 h-full overflow-x-auto ${className}`} {...dragTracking}>
        {columns.map((column) => renderColumn(column, false))}
      </div>
    </KanbanDensityProvider>
  );
}
